import React from 'react';

import cs from 'classnames';

import {ListMessage} from 'app/components/general/ListMessage';
import {DocumentFoldersList} from 'app/folders/components/DocumentFoldersList';
import {DocHeaderStructure} from 'app/general/structures/DocHeader';
import MetaContentTypeName from 'app/general/structures/MetaContentTypeName';
import {UserContentContainer} from 'app/general/structures/UserContentContainer';
import {ImagesCarousel} from 'app/general/widgets/ImagesCarousel';
import {DocumentPolicyList} from 'app/policies/components/DocumentPolicyList';
import {useProcedureByHashid} from 'app/procedures/api';
import {StyledStepsListDIV} from 'app/procedures/components/structures';
import {ViewableChoices} from 'app/procedures/components/ViewableChoices';
import {NextStepLinkItem} from 'app/steps/components/NextStepLinkItem';
import {DocumentTagsList} from 'app/tags/components/DocumentTagsList';
import {getProcedureName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';
import {isRightToLeftWrapper} from 'app/utils/rtl';

import {TemplateProcedureStep} from './TemplateProcedureStep';

import {useTemplate} from '../context';

export function TemplateProcedureDetail() {
  const template = useTemplate();
  const {data: procedure} = useProcedureByHashid(template.document_hashid);

  if (procedure == null) {
    return null;
  }

  const content = {
    name: procedure.name,
    url: procedure.url,
    html_url: procedure.html_url,
    is_large: procedure.is_large,
    description: procedure.description,
    content: procedure.content,
    lanes: procedure.lanes,
  };

  if (content == null) {
    return null;
  }

  const procedureTitle =
    typeof content.name === 'string'
      ? getProcedureName(content, true)
      : content.name;

  const embeds = [...(content.description.embeds ?? [])];
  const carousel = [...content.description.images, ...embeds];

  return (
    <>
      <DocHeaderStructure
        thumbnail={procedure.thumbnail}
        advancedContent={<MetaContentTypeName contentType={'procedure'} />}
        tagsContent={
          <>
            <DocumentTagsList tags={procedure.tags} />
            <DocumentFoldersList connections={procedure.connections} />
          </>
        }
        titleContent={
          <h1
            className={cs('fr-view', {
              'right-to-left-text': isRightToLeftWrapper(content.name),
            })}
          >
            <UserContentContainer content={procedureTitle} />
          </h1>
        }
        policyContent={<DocumentPolicyList policies={procedure.policies} />}
        descriptionContent={
          <>
            <UserContentContainer content={content.description.content} />
            {carousel.length > 0 && (
              <div onClick={stopPropagation}>
                <ImagesCarousel images={carousel} className="step-images" />
              </div>
            )}
          </>
        }
      />
      <StyledStepsListDIV className="js-document-body container pt-0">
        <div className="row">
          <div className="pt-4 col-12">
            <ol className="steps">
              {content.content.map((step, index) => (
                <li key={index} id={step.id}>
                  <TemplateProcedureStep
                    index={index}
                    step={step}
                    objectId={procedure.id}
                    lanes={content.lanes}
                    choicesList={
                      step.type === 'decision' ? (
                        <ViewableChoices
                          choices={step.choices}
                          steps={content.content}
                          edit={true}
                        />
                      ) : null
                    }
                    next={
                      <NextStepLinkItem step={step} steps={content.content} />
                    }
                  />
                </li>
              ))}
            </ol>

            {content.content.length === 0 ? (
              <ListMessage gray={true}>
                {gt('No steps for this procedure')}
              </ListMessage>
            ) : null}
          </div>
        </div>
      </StyledStepsListDIV>
    </>
  );
}

function stopPropagation(e: React.MouseEvent<HTMLDivElement>) {
  e.stopPropagation();
}
